/*!
=========================================================
* Regiform IMS Frontend - v1.0.0
=========================================================
* Updated for modern React development with:
* - React Router v6
* - React Query (TanStack Query)
* - Zustand state management
* - Ant Design UI components
=========================================================
*/
import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ConfigProvider } from "antd";

// Auth pages — loaded eagerly (entry points)
import SignUp from "./pages/SignUp";
import SignIn from "./pages/SignIn";
import PasswordResetRequest from "./pages/PasswordResetRequest";
import PasswordResetForm from "./pages/PasswordResetForm";
import ChangePassword from "./pages/ChangePassword";

// Import layout
import Main from "./components/layout/Main";
import {
  ProtectedRoute,
  PrivilegeGuard,
  AdminRoute,
  SuperAdminRoute,
  ErrorBoundary,
  ResizeObserverErrorBoundary,
  SessionTimeoutMonitor,
} from "./components/common";
import PrintIndicator from "./components/common/PrintIndicator";
import BottomNotification from "./components/common/BottomNotification";
import FeatureGate from "./components/common/FeatureGate";
import { initializeErrorSuppression } from "./utils/errorSuppression";

// Import query client configuration
import { queryClient } from "./config/queryClient";

// Import stores for initialization
import { useAuthStore } from "./stores/authStore";

// Import styles
import "antd/dist/reset.css";
import "./assets/styles/main.css";
import "./assets/styles/responsive.css";

// All other pages — lazy-loaded for faster startup
const Home = React.lazy(() => import("./pages/Home"));
const Tables = React.lazy(() => import("./pages/Tables"));
const Billing = React.lazy(() => import("./pages/Billing"));
const Rtl = React.lazy(() => import("./pages/Rtl"));
const Profile = React.lazy(() => import("./pages/Profile"));
const Partners = React.lazy(() => import("./pages/Partners"));
const Items = React.lazy(() => import("./pages/Items"));
const PriceGroups = React.lazy(() => import("./pages/PriceGroups"));
const Invoice = React.lazy(() => import("./pages/Invoice"));
const ProformaInvoice = React.lazy(() => import("./pages/ProformaInvoice"));
const SalesReturn = React.lazy(() => import("./pages/SalesReturn"));
const GoodReceivedNote = React.lazy(() => import("./pages/GoodReceivedNote"));
const PurchaseOrder = React.lazy(() => import("./pages/PurchaseOrder"));
const PaymentReceipt = React.lazy(() => import("./pages/PaymentReceipt"));
const RefundVoucher = React.lazy(() => import("./pages/RefundVoucher"));
const PettyCash = React.lazy(() => import("./pages/PettyCash"));
const PettyCashCreate = React.lazy(() =>
  import("./pages/PettyCash").then((m) => ({ default: m.PettyCashCreate })),
);
const SupplierPayment = React.lazy(() => import("./pages/SupplierPayment"));
const DeliveryOrder = React.lazy(() => import("./pages/DeliveryOrder"));
const SalesHistory = React.lazy(() => import("./pages/SalesHistory"));
const SalesReturnHistory = React.lazy(() => import("./pages/SalesReturnHistory"));
const WastageReport = React.lazy(() => import("./pages/WastageReport"));
const MaterialIssueReport = React.lazy(() => import("./pages/MaterialIssueReport"));
const ReturnToStoreReport = React.lazy(() => import("./pages/ReturnToStoreReport"));
const ProductionCompletionReport = React.lazy(() => import("./pages/ProductionCompletionReport"));
const CustomerPwoOrderReport = React.lazy(() => import("./pages/CustomerPwoOrderReport"));
const MaterialIssueDetailsReport = React.lazy(() => import("./pages/MaterialIssueDetailsReport"));
const CancelledDocuments = React.lazy(() => import("./pages/CancelledDocuments"));
const CustomerWiseSales = React.lazy(() => import("./pages/CustomerWiseSales"));
const SalesOutstandingAging = React.lazy(() => import("./pages/SalesOutstandingAging/SalesOutstandingAging"));
const PurchaseOutstandingAging = React.lazy(() => import("./pages/PurchaseOutstandingAging"));
const SupplierAdvances = React.lazy(() => import("./pages/SupplierAdvances"));
const CustomerAdvances = React.lazy(() => import("./pages/CustomerAdvances"));
const StockInHand = React.lazy(() => import("./pages/StockInHand/StockInHand"));
const StockLedger = React.lazy(() => import("./pages/StockLedger/StockLedger"));
const CustomerLedger = React.lazy(() => import("./pages/CustomerLedger/CustomerLedger"));
const SupplierLedger = React.lazy(() => import("./pages/SupplierLedger/SupplierLedger"));
const BillOfMaterials = React.lazy(() => import("./pages/BillOfMaterials").then(m => ({ default: m.BillOfMaterials })));
const ProductionOrder = React.lazy(() => import("./pages/ProductionOrder/ProductionOrder"));
const MaterialIssueNote = React.lazy(() => import("./pages/MaterialIssueNote/MaterialIssueNote"));
const ProductionCompletionNote = React.lazy(() => import("./pages/ProductionCompletionNote/ProductionCompletionNote"));
const Wastage = React.lazy(() => import("./pages/Wastage/Wastage"));
const ReturnToStore = React.lazy(() => import("./pages/ReturnToStore/ReturnToStore"));
const InventoryTransfer = React.lazy(() => import("./pages/InventoryTransfer/InventoryTransfer"));
const StockReconciliation = React.lazy(() => import("./pages/StockReconciliation/StockReconciliation"));
const InventoryIssue = React.lazy(() => import("./pages/InventoryIssue/InventoryIssue"));
const ProjectWorkOrder = React.lazy(() => import("./pages/ProjectWorkOrder/ProjectWorkOrder"));
const ProductionExpense = React.lazy(() => import("./pages/ProductionExpense/ProductionExpense"));
const ApInvoice = React.lazy(() => import("./pages/ApInvoice/ApInvoice"));
const ExpenseCategory = React.lazy(() => import("./pages/ExpenseCategory/ExpenseCategory"));
const BanksPage = React.lazy(() => import("./pages/Banks/Banks"));
const CurrenciesPage = React.lazy(() => import("./pages/Currencies/Currencies"));
const ExchangeRatesPage = React.lazy(() => import("./pages/ExchangeRates/ExchangeRates"));
const ChartOfAccounts = React.lazy(() => import("./pages/Accounts").then(m => ({ default: m.ChartOfAccounts })));
const JournalEntries = React.lazy(() => import("./pages/JournalEntries").then(m => ({ default: m.JournalEntries })));
const FinancialYears = React.lazy(() => import("./pages/FinancialYears"));
const AccountDetermination = React.lazy(() => import("./pages/AccountDetermination"));
const TrialBalance = React.lazy(() => import("./pages/TrialBalance/TrialBalance"));
const ProfitAndLoss = React.lazy(() => import("./pages/ProfitAndLoss/ProfitAndLoss"));
const SerialNumbers = React.lazy(() => import("./pages/SerialNumbers").then(m => ({ default: m.SerialNumbers })));
const Users = React.lazy(() => import("./pages/Administrator/Users/Users"));
const FilterDefaults = React.lazy(() => import("./pages/Administrator/FilterDefaults/FilterDefaults"));
const UserPrivileges = React.lazy(() => import("./pages/Administrator/UserPrivileges/UserPrivileges"));
const SystemFeatures = React.lazy(() => import("./pages/Administrator/SystemFeatures/SystemFeatures"));
const DocumentSequences = React.lazy(() => import("./pages/Administrator/DocumentSequences/DocumentSequences"));
const SystemSetup = React.lazy(() => import("./pages/SystemSetup"));

// Ant Design theme configuration — primary color driven by env var
const primaryColor = process.env.REACT_APP_PRIMARY_COLOR || "#1890ff";
const bgColor = process.env.REACT_APP_BG_COLOR || "";
const antdTheme = {
  token: {
    colorPrimary: primaryColor,
    borderRadius: 6,
    ...(bgColor && { colorBgLayout: bgColor }),
  },
};

function App() {
  // Initialize auth state on app start
  React.useEffect(() => {
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      // Optionally validate token or refresh user data
      console.log("User is authenticated");
    }

    // Sync CSS custom properties with env-driven colors
    document.documentElement.style.setProperty("--primary-color", primaryColor);
    if (bgColor) {
      document.documentElement.style.setProperty("--bg-layout", bgColor);
    }

    // Set favicon from logo URL if configured
    const logoUrl = process.env.REACT_APP_LOGO_URL;
    if (logoUrl) {
      const favicon = document.querySelector("link[rel='icon']");
      if (favicon) favicon.href = logoUrl;
    }

    // Initialize error suppression for harmless errors
    initializeErrorSuppression();

    // Global error handlers to prevent UI freezing
    const handleUnhandledRejection = (event) => {
      console.error("Unhandled promise rejection:", event.reason);
      // Prevent the default behavior (which would cause UI freezing)
      event.preventDefault();
    };

    // Add global error listeners
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    // Global fix: add a visible horizontal scrollbar below any Ant Design
    // table whose content overflows. Deferred to avoid blocking initial render.
    let tableObserver;
    const idleCallback = (window.requestIdleCallback || ((cb) => setTimeout(cb, 0)))(() => {
      const attachedTables = new WeakSet();

      const syncScrollbar = (tableContent, thumb, track) => {
        const { scrollWidth, clientWidth, scrollLeft } = tableContent;
        if (scrollWidth <= clientWidth) {
          track.style.display = "none";
          return;
        }
        track.style.display = "block";
        const ratio = clientWidth / scrollWidth;
        thumb.style.width = `${ratio * 100}%`;
        thumb.style.left = `${(scrollLeft / scrollWidth) * 100}%`;
      };

      const attachScrollbar = (tableContent) => {
        if (attachedTables.has(tableContent)) return;
        attachedTables.add(tableContent);

        const container = tableContent.parentElement;
        if (!container) return;

        const track = document.createElement("div");
        track.className = "app-table-scrollbar-track";
        const thumb = document.createElement("div");
        thumb.className = "app-table-scrollbar-thumb";
        track.appendChild(thumb);
        container.appendChild(track);

        const update = () => syncScrollbar(tableContent, thumb, track);
        update();

        tableContent.addEventListener("scroll", update, { passive: true });
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(tableContent);
        resizeObserver.observe(tableContent.firstElementChild || tableContent);

        // Drag handling for the thumb
        let dragStartX = 0;
        let dragStartScroll = 0;
        const onMouseMove = (e) => {
          const deltaX = e.clientX - dragStartX;
          const scrollDelta =
            (deltaX / tableContent.clientWidth) * tableContent.scrollWidth;
          tableContent.scrollLeft = dragStartScroll + scrollDelta;
        };
        const onMouseUp = () => {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
          document.body.style.userSelect = "";
        };
        thumb.addEventListener("mousedown", (e) => {
          dragStartX = e.clientX;
          dragStartScroll = tableContent.scrollLeft;
          document.body.style.userSelect = "none";
          document.addEventListener("mousemove", onMouseMove);
          document.addEventListener("mouseup", onMouseUp);
          e.preventDefault();
        });
        // Click on track jumps scroll position
        track.addEventListener("mousedown", (e) => {
          if (e.target === thumb) return;
          const rect = track.getBoundingClientRect();
          const clickRatio = (e.clientX - rect.left) / rect.width;
          tableContent.scrollLeft =
            clickRatio * tableContent.scrollWidth - tableContent.clientWidth / 2;
        });
      };

      const scanForTables = (root) => {
        if (!root || root.nodeType !== 1) return;
        const targets = root.classList?.contains("ant-table-content")
          ? [root]
          : root.querySelectorAll?.(".ant-table-content") || [];
        targets.forEach(attachScrollbar);
      };

      scanForTables(document.body);

      tableObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            scanForTables(node);
          }
        }
      });
      tableObserver.observe(document.body, { childList: true, subtree: true });
    });

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
      (window.cancelIdleCallback || clearTimeout)(idleCallback);
      if (tableObserver) tableObserver.disconnect();
    };
  }, []);

  return (
    <ResizeObserverErrorBoundary>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ConfigProvider theme={antdTheme}>
            <SessionTimeoutMonitor />
            <div className="App">
              <Suspense fallback={null}>
              <Routes>
                {/* Public routes */}
                <Route path="/sign-up" element={<SignUp />} />
                <Route path="/sign-in" element={<SignIn />} />
                <Route path="/password-reset-request" element={<PasswordResetRequest />} />
                <Route path="/reset-password" element={<PasswordResetForm />} />
                <Route path="/change-password" element={<ChangePassword />} />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Main />
                    </ProtectedRoute>
                  }
                >
                  <Route element={<PrivilegeGuard />}>
                  <Route path="dashboard" element={<Home />} />
                  <Route path="partners" element={<Partners />} />
                  <Route path="items" element={<Items />} />
                  <Route path="price-groups" element={<PriceGroups />} />
                  <Route
                    path="price-groups/edit/:priceGroupId"
                    element={<PriceGroups />}
                  />
                  <Route path="invoice" element={<Invoice />} />
                  <Route
                    path="invoices/edit/:invoiceId"
                    element={<Invoice />}
                  />
                  <Route path="proforma-invoice" element={<ProformaInvoice />} />
                  <Route
                    path="proforma-invoice/:proformaInvoiceId"
                    element={<ProformaInvoice />}
                  />
                  <Route path="sales-return" element={<SalesReturn />} />
                  <Route
                    path="sales-returns/edit/:salesReturnId"
                    element={<SalesReturn />}
                  />
                  <Route path="grn" element={<GoodReceivedNote />} />
                  <Route
                    path="grn/edit/:grnId"
                    element={<GoodReceivedNote />}
                  />
                  <Route path="purchase-orders" element={<PurchaseOrder />} />
                  <Route
                    path="purchase-orders/edit/:poId"
                    element={<PurchaseOrder />}
                  />
                  <Route path="payment-receipt" element={<PaymentReceipt />} />
                  <Route
                    path="payment-receipt/:receiptId"
                    element={<PaymentReceipt />}
                  />
                  <Route
                    path="refund-voucher"
                    element={
                      <FeatureGate featureKey="refund_voucher">
                        <RefundVoucher />
                      </FeatureGate>
                    }
                  />
                  <Route path="petty-cash" element={<PettyCash />} />
                  <Route
                    path="petty-cash/new"
                    element={<PettyCashCreate mode="CREATE" />}
                  />
                  <Route
                    path="petty-cash/edit/:id"
                    element={<PettyCashCreate mode="EDIT" />}
                  />
                  <Route path="supplier-payment" element={<SupplierPayment />} />
                  <Route
                    path="supplier-payment/:paymentId"
                    element={<SupplierPayment />}
                  />
                  <Route path="delivery-order" element={<DeliveryOrder />} />
                  <Route path="sales-history" element={<SalesHistory />} />
                  <Route path="sales-return-history" element={<SalesReturnHistory />} />
                  <Route path="wastage-report" element={<WastageReport />} />
                  <Route
                    path="material-issue-report"
                    element={<MaterialIssueReport />}
                  />
                  <Route
                    path="return-to-store-report"
                    element={<ReturnToStoreReport />}
                  />
                  <Route
                    path="production-completion-report"
                    element={<ProductionCompletionReport />}
                  />
                  <Route
                    path="reports/customer-pwo-orders"
                    element={<CustomerPwoOrderReport />}
                  />
                  <Route
                    path="material-issue-details-report"
                    element={<MaterialIssueDetailsReport />}
                  />
                  <Route
                    path="cancelled-documents"
                    element={<CancelledDocuments />}
                  />
                  <Route
                    path="customer-wise-sales"
                    element={<CustomerWiseSales />}
                  />
                  <Route
                    path="sales-outstanding-aging"
                    element={<SalesOutstandingAging />}
                  />
                  <Route
                    path="purchase-outstanding-aging"
                    element={<PurchaseOutstandingAging />}
                  />
                  <Route path="supplier-advances" element={<SupplierAdvances />} />
                  <Route path="customer-advances" element={<CustomerAdvances />} />
                  <Route path="stock-in-hand" element={<StockInHand />} />
                  <Route path="stock-ledger" element={<StockLedger />} />
                  <Route path="serial-numbers" element={<SerialNumbers />} />
                  <Route path="customer-ledger" element={<CustomerLedger />} />
                  <Route path="supplier-ledger" element={<SupplierLedger />} />
                  <Route path="bom" element={<BillOfMaterials />} />
                  <Route path="bom/:bomId" element={<BillOfMaterials />} />
                  <Route path="production-order" element={<ProductionOrder />} />
                  <Route path="production-order/:poId" element={<ProductionOrder />} />
                  <Route path="material-issue-note" element={<MaterialIssueNote />} />
                  <Route path="material-issue-note/:minId" element={<MaterialIssueNote />} />
                  <Route path="production-completion-note" element={<ProductionCompletionNote />} />
                  <Route path="production-completion-note/:pcnId" element={<ProductionCompletionNote />} />
                  <Route path="wastage" element={<Wastage />} />
                  <Route path="wastage/:wastageId" element={<Wastage />} />
                  <Route path="return-to-store" element={<ReturnToStore />} />
                  <Route path="return-to-store/:returnToStoreId" element={<ReturnToStore />} />
                  <Route path="inventory-transfer" element={<InventoryTransfer />} />
                  <Route path="inventory-transfer/:transferId" element={<InventoryTransfer />} />
                  <Route path="stock-reconciliation" element={<StockReconciliation />} />
                  <Route path="stock-reconciliations/edit/:reconciliationId" element={<StockReconciliation />} />
                  <Route path="inventory-issue" element={<InventoryIssue />} />
                  <Route path="inventory-issues/edit/:issueId" element={<InventoryIssue />} />
                  <Route path="project-work-orders" element={<ProjectWorkOrder />} />
                  <Route path="project-work-orders/:projectWorkOrderId" element={<ProjectWorkOrder />} />
                  <Route path="production-expenses" element={<ProductionExpense />} />
                  <Route path="production-expenses/:productionExpenseId" element={<ProductionExpense />} />
                  <Route path="ap-invoices" element={<ApInvoice />} />
                  <Route path="ap-invoices/:apInvoiceId" element={<ApInvoice />} />
                  <Route path="expense-categories" element={<ExpenseCategory />} />
                  <Route path="accounts/banks" element={<BanksPage />} />
                  <Route path="accounts/currencies" element={<CurrenciesPage />} />
                  <Route path="accounts/exchange-rates" element={<ExchangeRatesPage />} />
                  <Route path="accounts/chart-of-accounts" element={<ChartOfAccounts />} />
                  <Route path="accounts/journal-entries" element={<JournalEntries />} />
                  <Route path="accounts/financial-years" element={<FinancialYears />} />
                  <Route path="accounts/account-determination" element={<AccountDetermination />} />
                  <Route path="accounts/trial-balance" element={<TrialBalance />} />
                  <Route path="accounts/profit-and-loss" element={<ProfitAndLoss />} />
                  {/* <Route path="accounts/balance-sheet" element={<BalanceSheet />} /> */}
                  <Route
                    path="administrator/users"
                    element={
                      <AdminRoute>
                        <Users />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="administrator/system-setup"
                    element={
                      <AdminRoute>
                        <SystemSetup />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="administrator/filter-defaults"
                    element={
                      <AdminRoute>
                        <FilterDefaults />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="administrator/user-privileges"
                    element={
                      <AdminRoute>
                        <UserPrivileges />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="administrator/system-features"
                    element={
                      <SuperAdminRoute>
                        <SystemFeatures />
                      </SuperAdminRoute>
                    }
                  />
                  <Route
                    path="administrator/document-sequences"
                    element={
                      <SuperAdminRoute>
                        <DocumentSequences />
                      </SuperAdminRoute>
                    }
                  />

                  <Route path="tables" element={<Tables />} />
                  <Route path="billing" element={<Billing />} />
                  <Route path="rtl" element={<Rtl />} />
                  <Route path="profile" element={<Profile />} />
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  </Route>
                </Route>

                {/* Catch all route */}
                <Route
                  path="*"
                  element={<Navigate to="/dashboard" replace />}
                />
              </Routes>
              </Suspense>
            </div>

            {/* React Query DevTools - temporarily disabled due to compatibility issues */}
            {/* {process.env.NODE_ENV === "development" && (
          <ReactQueryDevtools initialIsOpen={false} />
        )} */}

            {/* Global print indicator - shows at bottom when printing */}
            <PrintIndicator />
            {/* Global bottom notification bar */}
            <BottomNotification />
          </ConfigProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </ResizeObserverErrorBoundary>
  );
}

export default App;
